import getCurrentWebAppVersion from 'api/auth/getCurrentWebAppVersion'
import config from 'config'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'

const CHECK_VERSION_TIMEOUT_MS = 10 * 1000
const VERSION_CHECK_WAIT_MS = 1000
const RELOAD_ATTEMPT_KEY = 'dwa-version-reload-attempt'
let pendingCheck: Promise<string | null> | undefined

export function checkForNewAppVersion(): Promise<string | null> {
    const runningVersion = process.env.REACT_APP_RELEASE_VERSION?.trim()

    if (config.ENVIRONMENT === 'local' || !runningVersion) {
        return Promise.resolve(null)
    }

    // Concurrent sign-in attempts share one bounded request, not its result.
    if (!pendingCheck) {
        const controller = new AbortController()

        const timeoutId = setTimeout(
            () => controller.abort(),
            CHECK_VERSION_TIMEOUT_MS,
        )

        pendingCheck = getCurrentWebAppVersion(controller.signal)
            .then((deployment) => {
                if (!deployment) return null

                if (deployment.build !== runningVersion) return deployment.build

                try {
                    // A rollback allows the same deployment to return later.
                    sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
                } catch {
                    // A failed guard reset must not break version checks.
                }

                return null
            })
            .finally(() => {
                clearTimeout(timeoutId)
                pendingCheck = undefined
            })
    }

    return pendingCheck
}

export async function waitForAppVersionCheck(
    check: Promise<string | null>,
): Promise<string | null> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    try {
        return await Promise.race([
            check,
            new Promise<null>((resolve) => {
                timeoutId = setTimeout(
                    () => resolve(null),
                    VERSION_CHECK_WAIT_MS,
                )
            }),
        ])
    } catch {
        return null
    } finally {
        clearTimeout(timeoutId)
    }
}

export function reloadForNewAppVersion(targetVersion: string | null): boolean {
    const runningVersion = process.env.REACT_APP_RELEASE_VERSION?.trim()

    if (
        config.ENVIRONMENT === 'local' ||
        !runningVersion ||
        !targetVersion ||
        targetVersion === runningVersion
    ) {
        return false
    }

    const attempt = JSON.stringify([runningVersion, targetVersion])

    try {
        // Persist before reloading so a stale cached index cannot loop.
        if (sessionStorage.getItem(RELOAD_ATTEMPT_KEY) === attempt) return false

        sessionStorage.setItem(RELOAD_ATTEMPT_KEY, attempt)
    } catch {
        // Without a persistent guard, automatic reloads could loop.
        return false
    }

    OctaneLogger.info('Applying new app version', {
        currentVersion: runningVersion,
        targetVersion,
    })

    window.location.reload()

    return true
}
